import "$styles/index.css"
import { animate, inView, scroll, stagger } from "motion"
import { identify, startAnalytics, track } from "./analytics.js"
import { friendCode, openSpot } from "./spot.js"
import "./wishlist.js"

startAnalytics()

/*
 * Motion, after Apple's rules (Designing Fluid Interfaces):
 * - springs, critically damped (bounce 0) unless something was "thrown";
 * - always from the element's current value, so nothing jumps when interrupted;
 * - never blocks input, and feedback starts on the press (CSS :active);
 * - with reduced motion, none of it: content is simply there.
 */

const motionOK = document.documentElement.classList.contains("motion")
const settle = { type: "spring", bounce: 0, duration: 0.7 }
const snappy = { type: "spring", bounce: 0, duration: 0.45 }

// ── Glass nav: an edge appears once content scrolls under it ──
const nav = document.querySelector("[data-nav]")
if (nav) {
  const onScroll = () => nav.classList.toggle("is-scrolled", window.scrollY > 8)
  onScroll()
  window.addEventListener("scroll", onScroll, { passive: true })
}

// ── The logo goes home; on the home page itself, it glides back to the top ──
document.querySelectorAll("[data-home]").forEach((a) =>
  a.addEventListener("click", (e) => {
    if (location.pathname !== "/") return
    e.preventDefault()
    history.replaceState(null, "", "/")
    window.scrollTo({ top: 0, behavior: motionOK ? "smooth" : "auto" })
  }),
)

// ── "Join the waitlist" glides down to the form and puts the cursor in it ──
document.querySelectorAll('a[href="#waitlist"], a[href="/#waitlist"]').forEach((a) =>
  a.addEventListener("click", (e) => {
    const target = document.getElementById("waitlist")
    if (!target) return
    e.preventDefault()
    history.replaceState(null, "", "#waitlist")
    target.scrollIntoView({ behavior: motionOK ? "smooth" : "auto", block: "center" })
    const email = target.querySelector('input[type="email"]')
    // Focus after the glide, without a second jump.
    setTimeout(() => email?.focus({ preventScroll: true }), motionOK ? 700 : 0)
  }),
)

// ── Hero headline: words rise in, one after another ──
document.querySelectorAll("[data-split]").forEach((el) => {
  const text = el.textContent.trim()
  el.setAttribute("aria-label", text)
  el.innerHTML = text
    .split(/\s+/)
    .map((w) => `<span aria-hidden="true" style="display:inline-block;white-space:pre">${w} </span>`)
    .join("")
  if (motionOK) {
    animate(el.children, { opacity: [0, 1], y: [28, 0], filter: ["blur(8px)", "blur(0px)"] }, { ...settle, delay: stagger(0.06) })
  }
})

// ── Rise in on scroll ──
if (motionOK) {
  inView(
    "[data-rise]",
    (el) => {
      animate(el, { opacity: [0, 1], y: [24, 0] }, settle)
      // Items inside rise one after another.
      const items = el.querySelectorAll("[data-stack-item]")
      if (items.length) animate(items, { opacity: [0, 1], y: [16, 0], scale: [0.97, 1] }, { ...snappy, delay: stagger(0.08, { startDelay: 0.15 }) })
      // In the app the sticker is placed by hand, so it lands with a small bounce.
      const heart = el.querySelector("[data-heart]")
      if (heart) animate(heart, { scale: [0, 1], rotate: [-12, 0] }, { type: "spring", bounce: 0.35, duration: 0.6, delay: 0.35 })
      el.querySelectorAll("[data-bar]").forEach((bar, i) => {
        animate(bar, { scaleX: [0, 1] }, { ...settle, delay: 0.2 + i * 0.07 })
      })
    },
    { amount: 0.2 },
  )
}

// ── Hero: the mode switcher drives the sky, the phone and the colours ──
const hero = document.querySelector("[data-hero]")
const phone = document.querySelector("[data-phone]")
const seg = document.querySelector("[data-seg]")
if (hero && phone && seg) {
  const pill = seg.querySelector("[data-seg-pill]")
  const buttons = [...seg.querySelectorAll("[data-mode-btn]")]
  const states = [...phone.querySelectorAll("[data-phone-state]")]
  const inks = phone.querySelectorAll("[data-phone-ink]")
  const order = ["home", "out", "sleep"]
  let mode = "home"
  let auto = null
  let resume = null
  let onScreen = false
  let hovering = false

  const placePill = (btn, animated) => {
    const target = { x: btn.offsetLeft - 4, width: btn.offsetWidth }
    if (animated && motionOK) animate(pill, { x: target.x, width: `${target.width}px` }, snappy)
    else Object.assign(pill.style, { transform: `translateX(${target.x}px)`, width: `${target.width}px` })
  }

  const setMode = (next, animated = true) => {
    const prev = states.find((s) => s.dataset.phoneState === mode)
    const incoming = states.find((s) => s.dataset.phoneState === next)
    mode = next
    hero.dataset.mode = next
    phone.dataset.mode = next
    pill.style.background = next === "sleep" ? "#2b3157" : "#fff"
    buttons.forEach((b) => {
      const on = b.dataset.modeBtn === next
      b.setAttribute("aria-selected", String(on))
      b.style.color = next === "sleep" ? (on ? "#ffd27a" : "#c8cbe8") : on ? "#2e2640" : "#6f6782"
    })
    inks.forEach((el) => (el.style.color = next === "sleep" ? "#eef0ff" : "#2e2640"))
    placePill(buttons.find((b) => b.dataset.modeBtn === next), animated)

    if (prev && prev !== incoming) {
      if (motionOK && animated) {
        animate(prev, { opacity: 0, y: -10, scale: 0.98 }, { duration: 0.25 }).then(() => {
          if (mode !== prev.dataset.phoneState) prev.hidden = true
        })
      } else {
        prev.hidden = true
      }
    }
    incoming.hidden = false
    if (motionOK && animated) animate(incoming, { opacity: [0, 1], y: [14, 0], scale: [0.98, 1] }, { ...settle, delay: 0.08 })
    else Object.assign(incoming.style, { opacity: 1, transform: "none" })
  }

  // It shows the three skies in turn, every 3 seconds, while the hero is on screen.
  // A tap takes over; it starts cycling again 8 seconds later. Pointing at the phone
  // or the switch holds it still, so it never moves out from under someone.
  function startAuto() {
    if (!motionOK || auto || !onScreen || hovering || resume) return
    auto = setInterval(() => setMode(order[(order.indexOf(mode) + 1) % order.length]), 3000)
  }
  function stopAuto() {
    clearInterval(auto)
    auto = null
  }
  const pick = (next) => {
    track("hero_mode_picked", { mode: next })
    stopAuto()
    clearTimeout(resume)
    resume = setTimeout(() => {
      resume = null
      startAuto()
    }, 8000)
    if (next !== mode) setMode(next)
  }
  for (const el of [seg, phone]) {
    el.addEventListener("pointerenter", () => {
      hovering = true
      stopAuto()
    })
    el.addEventListener("pointerleave", () => {
      hovering = false
      startAuto()
    })
  }

  buttons.forEach((b) => b.addEventListener("click", () => pick(b.dataset.modeBtn)))
  // Arrow keys move between modes, as a tab list should.
  seg.addEventListener("keydown", (e) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return
    const i = order.indexOf(mode) + (e.key === "ArrowRight" ? 1 : -1)
    const next = order[(i + order.length) % order.length]
    pick(next)
    buttons.find((b) => b.dataset.modeBtn === next).focus()
  })

  inView(hero, () => {
    onScreen = true
    startAuto()
    return () => {
      onScreen = false
      stopAuto()
    }
  })

  setMode("home", false)
  window.addEventListener("resize", () => placePill(buttons.find((b) => b.dataset.modeBtn === mode), false))
}

// ── "Why we built it": words light up as you read down ──
document.querySelectorAll("[data-lit]").forEach((section) => {
  const words = [...section.querySelectorAll(".lit-word")]
  if (!motionOK) {
    words.forEach((w) => w.classList.add("on"))
    return
  }
  scroll(
    (progress) => {
      const lit = Math.ceil(progress * words.length)
      words.forEach((w, i) => w.classList.toggle("on", i < lit))
    },
    { target: section, offset: ["start 85%", "end 85%"] },
  )
})

// ── How it works: each illustration's pieces pop in, one after another ──
if (motionOK) {
  inView(
    "[data-illus]",
    (el) => {
      animate(el.querySelectorAll("[data-pop]"), { opacity: [0, 1], scale: [0.85, 1], y: [10, 0] }, { type: "spring", bounce: 0.25, duration: 0.6, delay: stagger(0.12, { startDelay: 0.2 }) })
    },
    { amount: 0.4 },
  )
}

// ── Waitlist ──
document.querySelectorAll("[data-waitlist]").forEach((form) => {
  const msg = form.querySelector("[data-waitlist-msg]")
  const button = form.querySelector('button[type="submit"]')
  const email = form.querySelector('input[name="email"]')
  const slot = form.querySelector("[data-turnstile]")
  let widget = null
  // Turnstile, when a site key is set (site.metadata.turnstile_sitekey).
  if (slot) {
    const render = () => {
      if (window.turnstile && widget === null) widget = window.turnstile.render(slot, { sitekey: slot.dataset.sitekey, size: "flexible" })
    }
    window.turnstile ? render() : window.addEventListener("load", render)
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault()
    msg.textContent = ""
    if (!email.value.trim() || !email.checkValidity()) {
      msg.textContent = "That email doesn't look right."
      if (motionOK) animate(email.parentElement, { x: [0, -8, 8, -5, 5, 0] }, { duration: 0.4 })
      email.focus()
      return
    }
    button.disabled = true
    const label = button.textContent
    button.textContent = "Joining…"
    try {
      const data = new FormData(form)
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: data.get("email"),
          platform: data.get("platform"),
          // So emails arrive in their daytime, not at 3am.
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          company: data.get("company"),
          ref: data.get("ref") || new URLSearchParams(location.search).get("ref") || undefined,
          w: friendCode(),
          token: widget !== null ? window.turnstile.getResponse(widget) : undefined,
        }),
      })
      const out = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(out.error || "Something went wrong. Try again.")
      form.classList.add("is-done")
      const check = form.querySelector("[data-check]")
      if (motionOK) animate(check, { scale: [0, 1], rotate: [-20, 0] }, { type: "spring", bounce: 0.4, duration: 0.6 })
      // Then their spot in the queue, and the ways to move up.
      track("waitlist_joined", { position: out.position, total: out.total, tier: out.tier?.name ?? null, from_friend: !!friendCode(), platform: data.get("platform"), form: form.id || null })
      identify(out.code, { position: out.position, tier: out.tier?.name ?? null, referrals: out.referrals ?? 0 })
      if (out.code) setTimeout(() => openSpot({ ...out, email: data.get("email") }), motionOK ? 650 : 0)
    } catch (err) {
      msg.textContent = err.message
      if (widget !== null) window.turnstile.reset(widget)
    } finally {
      button.disabled = false
      button.textContent = label
    }
  })
})

// ── The heart at the bottom beats, gently, while it's on screen ──
const beat = document.querySelector("[data-beat]")
if (beat && motionOK) {
  inView(beat, () => {
    const controls = animate(beat, { scale: [1, 1.08, 1, 1.05, 1] }, { duration: 1.6, repeat: Infinity, repeatDelay: 1.2, ease: "easeInOut" })
    return () => controls.stop()
  })
}
